/**
 * Verse study handler — deterministic DB lookup, no ML inference.
 */

import { runInAction } from 'mobx';
import { fetchVerseStudy } from '@api/verse-study';
import type { ChatState } from './chat-state';

/** Add a verse study message */
export async function addVerseStudy(state: ChatState, reference: string): Promise<void> {
  state.clearHistory();
  // clearHistory readies the composer for a fresh chat; a programmatic
  // study flow keeps it hidden (same rule as recalled commentary).
  state.isComposerVisible = false;
  const loadingId = state.nextId();
  state.messages.push({
    id: loadingId,
    role: 'assistant',
    content: '',
    timestamp: new Date().toISOString(),
    isLoading: true,
  });
  state.isLoading = true;

  try {
    const response = await fetchVerseStudy(reference);
    runInAction(() => {
      const idx = state.messages.findIndex((m) => m.id === loadingId);
      if (idx !== -1) {
        state.messages[idx] = {
          id: loadingId,
          role: 'assistant',
          content: response.verses.map((v) => `${v.reference}: ${v.text}`).join('\n'),
          timestamp: new Date().toISOString(),
          scriptureResponse: response,
          commentaryState: 'not-requested',
          intent: 'verse_study',
          originalQuery: `What does ${reference} mean?`,
        };
      }
      state.isLoading = false;
      state.persistLocal();
    });
  } catch {
    runInAction(() => {
      const idx = state.messages.findIndex((m) => m.id === loadingId);
      if (idx !== -1) {
        state.messages[idx] = {
          id: loadingId,
          role: 'assistant',
          content: 'Unable to load verse study. Please try again later.',
          timestamp: new Date().toISOString(),
          isError: true,
        };
      }
      state.isLoading = false;
      state.persistLocal();
    });
  }
}
